import React, { useEffect } from "react";

import {
  Layout,
  Padding,
  Pagination,
  Sheet,
  Text,
  Map,
  Button,
  Icon,
  IconList,
  Container,
} from "@kidslivesafe/sc-react";

import "./market-search.css";
import PreForeclosureCard from "../../../components/UIComponents/PreForeclosureCard/PreForeclosureCard";
import PreForeclosurePopUp from "../../../components/UIComponents/PreForeclosurePopUp/PreForeclosurePopUp";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectBrandInfo,
  selectMapboxKey,
} from "../../../features/brand/brandSlice";
import {
  preForeclosureSearchAsync,
  selectPreForeclosureData,
  setPreForeclosureSearchType,
} from "../../../features/reports/reportsSlice";

import { selectPreForeclosureSearchParams } from "../../../features/multisearch/multisearchSlice";

import { preForeclosureData } from "../../../features/reports/reportTypes";
import {
  formatAddress,
  generateList,
  useCurrentBreakpoint,
} from "@kidslivesafe/sc-foundation";
import { usePropertySearchDispatch } from "../../../hooks/propertyReportHook";
import { MemberEvent } from "../../../features/admin/adminTypes";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";

interface PinData {
  latitude: number;
  longitude: number;
  popup: React.ReactNode;
  element?: React.ReactNode;
}

// debounce function to limit the rate of API calls
function debounce<T extends (...args: any[]) => void>(func: T, delay: number) {
  let timer: ReturnType<typeof setTimeout>;
  const debounced = (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

const MarketSearch: React.FC = () => {
  // const [sort, setSort] = React.useState<string | undefined>("recording-date");
  const [page, setPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(0);
  const [currentPageData, setCurrentPageData] = React.useState<
    preForeclosureData[]
  >([]);
  const mapboxToken = useAppSelector(selectMapboxKey);
  const [center, setCenter] = React.useState<number[]>([-98.789062, 38.548165]);
  const [zoom, setZoom] = React.useState<number>(11);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const [mapPins, setMapPins] = React.useState<PinData[]>([]);
  const preForeclosureDataPoints = useAppSelector(selectPreForeclosureData);
  const [currentDisplay, setCurrentDisplay] = React.useState<"map" | "list">(
    "map",
  );
  const brandInfo = useAppSelector(selectBrandInfo);
  const pullPropertyReport = usePropertySearchDispatch();
  const preForeclosureSearchParams = useAppSelector(
    selectPreForeclosureSearchParams,
  );

  const currentBreakpoint = useCurrentBreakpoint();

  const memberEventMarketSearch = (event: MemberEvent) => {
    dispatch(sendMemberEventAsync(event));
  };

  useEffect(() => {
    if (preForeclosureSearchParams) {
      setCurrentDisplay("map");
      setCenter([
        preForeclosureSearchParams.longitude || -98.789062,
        preForeclosureSearchParams.latitude || 38.548165,
      ]);
      setSearchValue(
        formatAddress({
          address: {
            fullStreetAddress: generateList(
              [
                {
                  value: preForeclosureSearchParams.addressLine1
                    ? preForeclosureSearchParams.addressLine1
                    : "",
                  label: "",
                },
                {
                  value: preForeclosureSearchParams.addressLine2
                    ? preForeclosureSearchParams.addressLine2
                    : "",
                  label: "",
                },
              ],
              "",
            ),
            city: preForeclosureSearchParams.city,
            state: preForeclosureSearchParams.state,
            zip: preForeclosureSearchParams.zip,
          },
        }),
      );
    }
  }, [preForeclosureSearchParams]);

  useEffect(() => {
    if (preForeclosureDataPoints) {
      // calculate total pages, assume page size of 30
      const totalPages = Math.ceil(preForeclosureDataPoints.length / 30);
      setTotalPages(totalPages);
      setPage(totalPages > 0 ? 1 : 0);
      setCurrentPageData(preForeclosureDataPoints.slice(0, 30));
    }
  }, [preForeclosureDataPoints]);

  useEffect(() => {
    if (preForeclosureDataPoints) {
      const mapPins = preparePinData(preForeclosureDataPoints);
      setMapPins(mapPins);
    }
  }, [page]);

  // handlePaginationChange
  const handlePaginationChange = (newPage: number) => {
    if (
      !preForeclosureDataPoints ||
      newPage < 1 ||
      newPage > Math.ceil(preForeclosureDataPoints.length / 30)
    ) {
      return;
    }
    setPage(newPage);
    const startIndex = (newPage - 1) * 30;
    const endIndex = startIndex + 30;
    setCurrentPageData(preForeclosureDataPoints.slice(startIndex, endIndex));
  };

  const dispatch = useAppDispatch();

  const preparePinData = (marketData: preForeclosureData[]) => {
    const startIndex = (page - 1) * 30;
    const endIndex = startIndex + 30;
    const pinData = marketData
      .map((entry, index) => {
        const latitude = Number(entry.latitude);
        const longitude = Number(entry.longitude);
        const inPage = index >= startIndex && index < endIndex;

        if (
          !isNaN(latitude) &&
          !isNaN(longitude) &&
          latitude !== 0 &&
          longitude !== 0 &&
          latitude >= 24.396308 &&
          latitude <= 49.384358 &&
          longitude >= -125.0 &&
          longitude <= -66.93457
        ) {
          return {
            latitude,
            longitude,
            popup: (
              <div id={`${entry.fullStreetAddress}--popup`}>
                <PreForeclosurePopUp
                  preForeclosureItem={entry}
                  key={index}
                  brandInfoMarkerColor={inPage ? "D94242" : "44A54D"}
                  pullPropertyReport={address =>
                    pullPropertyReport(address, "market search")
                  }
                  memberEventMarketSearch={memberEventMarketSearch}
                />
              </div>
            ),
            element: (
              <Text tag="div">
                <Icon
                  name={
                    inPage
                      ? IconList.IconGradientRed
                      : IconList.IconGradientGreen
                  }
                  size={18}
                />
              </Text>
            ),
          };
        } else {
          // Skip this entry if the coordinates are invalid
          return null;
        }
      })
      // Remove null entries
      .filter((pin): pin is Exclude<typeof pin, null> => pin !== null);
    return pinData;
  };
  const searchValueRef = React.useRef(searchValue);
  useEffect(() => {
    searchValueRef.current = searchValue;
  }, [searchValue]);

  const preForeclosureSearch = React.useCallback(
    (value: number[]) => {
      dispatch(
        preForeclosureSearchAsync({
          bbox: value,
          pageNumber: 1,
          pageSize: 200,
          searchPhrase: searchValueRef.current,
        }),
      )
        .unwrap()
        .then(result => {
          const pinDataHere = preparePinData(
            result.preForeclosureSearchResponse.result,
          );
          setMapPins(pinDataHere);
        })
        .catch(error => console.log(error.message));
    },
    [dispatch],
  );

  const debouncedPreForeclosureSearch = React.useMemo(() => {
    const debouncedFunction = debounce(preForeclosureSearch, 500);
    return debouncedFunction;
  }, [preForeclosureSearch]);

  useEffect(() => {
    return () => debouncedPreForeclosureSearch.cancel?.();
  }, [debouncedPreForeclosureSearch]);

  return (
    <>
      <Sheet>
        <Container>
          <Padding left="lg" right="lg" top="md" bottom="md">
            <Layout type="flex" flexProps={{ direction: "column", gap: "md" }}>
              {(currentBreakpoint !== "desktop" && currentDisplay === "map") ||
              currentBreakpoint === "desktop" ? (
                <Text fontWeight="semibold">
                  <Text color="base" tag="span" fontWeight="semibold">
                    {preForeclosureDataPoints
                      ? preForeclosureDataPoints.length
                      : "0"}
                  </Text>{" "}
                  Properties Displayed
                </Text>
              ) : null}
              {currentBreakpoint !== "desktop" && currentDisplay === "map" && (
                <Button
                  color="primary"
                  handleClick={() => {
                    setCurrentDisplay("list");
                  }}
                  buttonText={
                    <Layout
                      type="flex"
                      flexProps={{ gap: "xxs", alignItems: "center" }}
                    >
                      <Icon name={IconList.IconListLines} />
                      <Text>List</Text>
                    </Layout>
                  }
                  fill="fill"
                />
              )}
              {(currentBreakpoint !== "desktop" && currentDisplay === "map") ||
              currentBreakpoint === "desktop" ? (
                <Map
                  mapboxToken={mapboxToken}
                  center={center}
                  zoom={zoom}
                  searchBoxProps={{
                    useBbox: value => {
                      debouncedPreForeclosureSearch(value);
                    },
                    setCenter,
                    searchValue,
                    searchOnChange: value => {
                      setSearchValue(value);
                    },
                    setZoom,
                    searchBoxMiddleware: () => {
                      dispatch(setPreForeclosureSearchType("MapSearch"));
                    },
                  }}
                  markerData={mapPins ? mapPins : undefined}
                  legendProps={{
                    position: `${currentBreakpoint === "desktop" ? "LT" : "RB"}`,
                    border: true,
                  }}
                />
              ) : null}

              {((currentBreakpoint !== "desktop" &&
                currentDisplay === "list") ||
                currentBreakpoint === "desktop") && (
                <>
                  {preForeclosureDataPoints &&
                  preForeclosureDataPoints.length > 0 ? (
                    <>
                      {currentBreakpoint === "desktop" && (
                        <Pagination
                          currentPage={page}
                          totalPages={totalPages}
                          onPageChange={handlePaginationChange}
                        />
                      )}
                      <Layout
                        type="flex"
                        flexProps={{ justifyContent: "space-between" }}
                      >
                        <Text fontWeight="normal">
                          <Text color="base" tag="span" fontWeight="semibold">
                            {preForeclosureDataPoints.length > 0
                              ? `${(page - 1) * 30 + 1} - ${Math.min(page * 30, preForeclosureDataPoints.length)}`
                              : "0"}
                          </Text>{" "}
                          of{" "}
                          <Text color="base" tag="span" fontWeight="semibold">
                            {preForeclosureDataPoints
                              ? preForeclosureDataPoints.length
                              : "0"}
                          </Text>{" "}
                        </Text>
                        <Layout
                          type="flex"
                          flexProps={{ gap: "xs", alignItems: "center" }}
                        >
                          <Text fontWeight="semibold" textWrap="nowrap">
                            Sort By:
                          </Text>
                          <Text
                            color="base"
                            fontWeight="normal"
                            textWrap="nowrap"
                          >
                            Recording Date
                          </Text>
                          {/* <Select
                          value={sort}
                          selectStyle="flat"
                          onOptionSelected={value => setSort(value)}
                          options={[
                            {
                              value: "recording-date",
                              label: "Recording Date",
                            },
                          ]}
                        /> */}
                        </Layout>
                      </Layout>
                      {currentBreakpoint !== "desktop" &&
                        currentDisplay === "list" && (
                          <>
                            <Button
                              color="primary"
                              handleClick={() => {
                                setCurrentDisplay("map");
                              }}
                              buttonText={
                                <Layout
                                  type="flex"
                                  flexProps={{
                                    gap: "xxs",
                                    alignItems: "center",
                                  }}
                                >
                                  <Icon name={IconList.IconMap} />
                                  <Text>Map</Text>
                                </Layout>
                              }
                              fill="fill"
                            />
                            <Text size="xs" tag="i">
                              Return to map to search a new market.
                            </Text>
                          </>
                        )}
                      <Padding
                        space="none"
                        breakpointStyles={{ desktop: { bottom: "md" } }}
                      >
                        <Layout
                          type="grid"
                          gridProps={{ templateColumns: "1-column", gap: "lg" }}
                          breakpointStyles={{
                            desktop: {
                              type: "grid",
                              gridProps: {
                                templateColumns: "3-column",
                                gap: "lg",
                              },
                            },
                          }}
                        >
                          {currentPageData.length > 0 &&
                            currentPageData.map((item, index) => (
                              <PreForeclosureCard
                                preForeclosureItem={item}
                                key={index}
                                memberEventMarketSearch={
                                  memberEventMarketSearch
                                }
                              />
                            ))}
                        </Layout>
                      </Padding>
                      <Padding space="none" bottom="md">
                        <Pagination
                          currentPage={page}
                          totalPages={totalPages}
                          onPageChange={handlePaginationChange}
                        />
                      </Padding>
                    </>
                  ) : (
                    <>
                      {currentBreakpoint !== "desktop" &&
                        currentDisplay === "list" && (
                          <>
                            <Button
                              color="primary"
                              handleClick={() => {
                                setCurrentDisplay("map");
                              }}
                              buttonText={
                                <Layout
                                  type="flex"
                                  flexProps={{
                                    gap: "xxs",
                                    alignItems: "center",
                                  }}
                                >
                                  <Icon name={IconList.IconMap} />
                                  <Text>Map</Text>
                                </Layout>
                              }
                              fill="fill"
                            />
                            <Layout
                              type="flex"
                              flexProps={{ justifyContent: "center" }}
                            >
                              <Text fontWeight="semibold" textAlign="center">
                                There are no pre-foreclosures found in this
                                area. Return to your map to search for
                                pre-foreclosed properties.
                              </Text>
                            </Layout>
                          </>
                        )}
                      <Layout
                        type="flex"
                        flexProps={{ justifyContent: "center" }}
                      >
                        <Text fontWeight="semibold" textAlign="center">
                          Pan the map or search a new city, zip code, or address
                          to find pre-foreclosed properties.
                        </Text>
                      </Layout>
                    </>
                  )}
                </>
              )}
              {currentBreakpoint !== "desktop" &&
                currentDisplay === "map" &&
                preForeclosureDataPoints &&
                preForeclosureDataPoints.length === 0 && (
                  <Layout type="flex" flexProps={{ justifyContent: "center" }}>
                    <Text fontWeight="semibold" textAlign="center">
                      Pan the map or search a new city, zip code, or address to
                      find pre-foreclosed properties.
                    </Text>
                  </Layout>
                )}
            </Layout>
          </Padding>
        </Container>
      </Sheet>
    </>
  );
};

export default MarketSearch;
